import { defineMessages, MessageDescriptor } from 'react-intl';
import type { ReleaseSetupDetail, ReleaseSetupKey } from 'fym-shared/src/music24/releases';

/** The setup items of a release (design b1, b3) and why each one is not done. */
export const releaseSetupMessages = defineMessages({
  title: { id: 'app.sml.releases.setup.title', defaultMessage: 'Setup' },
  subtitle: { id: 'app.sml.releases.setup.subtitle', defaultMessage: 'Set in the Catalog song page' },
  missingSubtitle: {
    id: 'app.sml.releases.setup.missingSubtitle',
    defaultMessage: '{count, plural, one {# item missing} other {# items missing}}. Fix them in the Catalog song page.',
  },
  complete: { id: 'app.sml.releases.setup.complete', defaultMessage: 'Complete' },
  incomplete: { id: 'app.sml.releases.setup.incomplete', defaultMessage: 'Incomplete' },
  openInCatalog: { id: 'app.sml.releases.setup.openInCatalog', defaultMessage: 'Open in Catalog' },
  fixInCatalog: { id: 'app.sml.releases.setup.fixInCatalog', defaultMessage: 'Fix in Catalog' },
  // QA finding F03 (decision D10): one click makes the missing page, in place of "Fix in Catalog".
  makePresave: { id: 'app.sml.releases.setup.makePresave', defaultMessage: 'Make the pre-save page' },
  makeSmartLink: { id: 'app.sml.releases.setup.makeSmartLink', defaultMessage: 'Make the SmartLink' },
  working: { id: 'app.sml.releases.setup.working', defaultMessage: 'Working' },
});

/** The name of each item, in the list of ticks and in "Missing in Catalog: …". */
export const releaseSetupItemNames: Record<ReleaseSetupKey, MessageDescriptor> = defineMessages({
  master: { id: 'app.sml.releases.setup.item.master', defaultMessage: 'Master audio' },
  coverAndDate: { id: 'app.sml.releases.setup.item.coverAndDate', defaultMessage: 'Cover and release date' },
  lyrics: { id: 'app.sml.releases.setup.item.lyrics', defaultMessage: 'Lyrics with timing' },
  smartLink: { id: 'app.sml.releases.setup.item.smartLink', defaultMessage: 'SmartLink' },
  presave: { id: 'app.sml.releases.setup.item.presave', defaultMessage: 'Pre-save' },
});

/** The short lower-case names of "Missing in Catalog: master audio, lyrics, SmartLink" (design a1). */
export const releaseSetupShortNames: Record<ReleaseSetupKey, MessageDescriptor> = defineMessages({
  master: { id: 'app.sml.releases.setup.short.master', defaultMessage: 'master audio' },
  coverAndDate: { id: 'app.sml.releases.setup.short.coverAndDate', defaultMessage: 'cover and date' },
  lyrics: { id: 'app.sml.releases.setup.short.lyrics', defaultMessage: 'lyrics' },
  smartLink: { id: 'app.sml.releases.setup.short.smartLink', defaultMessage: 'SmartLink' },
  presave: { id: 'app.sml.releases.setup.short.presave', defaultMessage: 'pre-save' },
});

/** One sentence per reason (design b3). */
export const releaseSetupDetails: Record<ReleaseSetupDetail, MessageDescriptor> = defineMessages({
  noMaster: {
    id: 'app.sml.releases.setup.detail.noMaster',
    defaultMessage: 'No master yet. Videos and the AI reel need it.',
  },
  masterProcessing: {
    id: 'app.sml.releases.setup.detail.masterProcessing',
    defaultMessage: 'We are reading the master. It takes a few minutes.',
  },
  masterFailed: {
    id: 'app.sml.releases.setup.detail.masterFailed',
    defaultMessage: 'We could not read the master. Upload it again.',
  },
  noCover: {
    id: 'app.sml.releases.setup.detail.noCover',
    defaultMessage: 'The release date is set. The cover is not set.',
  },
  noDate: {
    id: 'app.sml.releases.setup.detail.noDate',
    defaultMessage: 'The cover is set. The release date is not set. Without it we cannot plan a rollout.',
  },
  noCoverNoDate: {
    id: 'app.sml.releases.setup.detail.noCoverNoDate',
    defaultMessage: 'The cover and the release date are not set.',
  },
  noLyrics: {
    id: 'app.sml.releases.setup.detail.noLyrics',
    defaultMessage: 'No lyrics yet. Lyric videos and cards need them.',
  },
  noTiming: {
    id: 'app.sml.releases.setup.detail.noTiming',
    defaultMessage: 'The lyrics are in. The line timing is not set. Lyric videos need it.',
  },
  timingSyncing: {
    id: 'app.sml.releases.setup.detail.timingSyncing',
    defaultMessage: 'We are timing the lyric lines.',
  },
  timingFailed: {
    id: 'app.sml.releases.setup.detail.timingFailed',
    defaultMessage: 'We could not time the lyric lines. Set the times by hand.',
  },
  noSmartLink: {
    id: 'app.sml.releases.setup.detail.noSmartLink',
    defaultMessage: 'No SmartLink. Posts have no link to the song.',
  },
  smartLinkDraft: {
    id: 'app.sml.releases.setup.detail.smartLinkDraft',
    defaultMessage: 'The SmartLink is a draft. Publish it so fans can open it.',
  },
  noPresave: {
    id: 'app.sml.releases.setup.detail.noPresave',
    defaultMessage: 'No pre-save. Fans cannot save the song before the release day.',
  },
});
